import { Euler, Matrix4, Quaternion, Vector3 } from 'three';

/**
 * Converts the MediaPipe facial transformation matrix (16 floats) into a
 * THREE.Matrix4.
 *
 * MediaPipe stores `packed_data` column-major (Eigen default), which is exactly
 * what Matrix4.fromArray expects. Because this has been a recurring source of
 * confusion across MediaPipe versions, the layout is auto-detected: in an
 * affine rigid transform the bottom row is (0,0,0,1) while the translation
 * (tens of centimetres) lives in the last column. Whichever triple carries the
 * larger magnitude is the translation.
 */
export function mediapipeMatrixToMatrix4(data: ArrayLike<number>, out = new Matrix4()): Matrix4 {
  if (data.length !== 16) throw new Error(`Expected 16 matrix values, got ${data.length}`);
  const colMajorTranslation = Math.abs(data[12]!) + Math.abs(data[13]!) + Math.abs(data[14]!);
  const rowMajorTranslation = Math.abs(data[3]!) + Math.abs(data[7]!) + Math.abs(data[11]!);
  out.fromArray(data as number[]);
  if (rowMajorTranslation > colMajorTranslation) out.transpose();
  return out;
}

const _sx = new Vector3();
const _sy = new Vector3();
const _sz = new Vector3();
const _m = new Matrix4();

/**
 * Extracts a unit-scale rotation quaternion from a (possibly scaled) affine
 * matrix. MediaPipe's matrix is rigid in theory; normalising the basis vectors
 * makes the code robust to any residual scale.
 */
export function rotationFromMatrix(m: Matrix4, out = new Quaternion()): Quaternion {
  m.extractBasis(_sx, _sy, _sz);
  _sx.normalize();
  _sy.normalize();
  _sz.normalize();
  _m.makeBasis(_sx, _sy, _sz);
  return out.setFromRotationMatrix(_m);
}

/** Translation column of an affine matrix. */
export function translationFromMatrix(m: Matrix4, out = new Vector3()): Vector3 {
  return out.setFromMatrixPosition(m);
}

/**
 * Yaw / pitch / roll (degrees) of a head rotation, using the camera frame
 * convention: yaw about +Y (left/right turn), pitch about +X (nod), roll about
 * +Z (tilt). Intrinsic YXZ order matches how a head moves.
 */
export function headAnglesDeg(q: Quaternion): { yaw: number; pitch: number; roll: number } {
  _euler.setFromQuaternion(q, 'YXZ');
  const r2d = 180 / Math.PI;
  return { yaw: _euler.y * r2d, pitch: _euler.x * r2d, roll: _euler.z * r2d };
}
const _euler = new Euler();
